import { forwardRef, type ComponentProps } from "react";

import { cn } from "@/lib/utils";

import type { AgentMarkState } from "./AgentMark";

export type RobotHue = "blue" | "green" | "amber" | "violet" | "rose" | "cyan";
export type RobotExpression =
  | "smile"
  | "grin"
  | "happy"
  | "lookSide"
  | "wink"
  | "curious";
export type RobotLook = { hue: RobotHue; expression: RobotExpression };

/** The six looks, in the order agents are assigned them by seed. */
export const ROBOT_LOOKS: readonly RobotLook[] = [
  { hue: "blue", expression: "smile" },
  { hue: "green", expression: "grin" },
  { hue: "amber", expression: "happy" },
  { hue: "violet", expression: "lookSide" },
  { hue: "rose", expression: "wink" },
  { hue: "cyan", expression: "curious" }
];

/** Casey's one face: blue, smiling. */
export const CASEY_LOOK: RobotLook = ROBOT_LOOKS[0];

type Props = Omit<ComponentProps<"svg">, "children" | "ref"> & {
  look?: RobotLook;
  /**
   * `idle` blinks and bobs; `thinking` tilts with darting eyes; `speaking`
   * talks; `static` holds still.
   */
  state?: AgentMarkState;
  /** Draw the hue's soft tile behind him, as an avatar. */
  tile?: boolean;
  /** Accessible name; omit for a purely decorative robot next to a label. */
  title?: string;
};

const EYE_L = { cx: 19.5, cy: 18.6 };
const EYE_R = { cx: 28.5, cy: 18.6 };

const Eye = ({
  cx,
  cy,
  big,
  side
}: typeof EYE_L & { big: boolean; side: boolean }) => {
  const x = cx + (side ? 0.8 : 0);
  return (
    <g className="agent-robot-eye">
      <circle cx={x} cy={cy} r={big ? 2.1 : 1.9} className="agent-robot-ink" />
      <circle
        cx={x - 0.6}
        cy={cy - 0.8}
        r={0.55}
        className="agent-robot-glint"
      />
    </g>
  );
};

/** An eye drawn as a stroke: `up` is the happy squint, otherwise a wink. */
const ClosedEye = ({ cx, cy, up }: typeof EYE_L & { up: boolean }) => (
  <path
    className="agent-robot-line"
    d={
      up
        ? `M${cx - 1.6} ${cy + 0.6} Q${cx} ${cy - 1.6} ${cx + 1.6} ${cy + 0.6}`
        : `M${cx - 1.6} ${cy - 0.2} Q${cx} ${cy + 1.2} ${cx + 1.6} ${cy - 0.2}`
    }
    strokeWidth={1.2}
    strokeLinecap="round"
  />
);

const Mouth = ({ expression }: { expression: RobotExpression }) => {
  if (expression === "curious")
    return (
      <ellipse
        cx={24}
        cy={22.6}
        rx={1}
        ry={1.2}
        className="agent-robot-mouth agent-robot-mouth-fill"
      />
    );
  if (expression === "lookSide")
    return (
      <path
        className="agent-robot-mouth agent-robot-mouth-line"
        d="M22.2 22.2 Q24.4 23.6 26.4 21.4"
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    );
  if (expression === "smile")
    return (
      <path
        className="agent-robot-mouth agent-robot-mouth-line"
        d="M22 21.6 Q24 23.6 26 21.6"
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    );
  return (
    <g className="agent-robot-mouth">
      <path
        className="agent-robot-mouth-fill"
        d="M21.6 21.1 H26.4 Q26.4 24.4 24 24.4 Q21.6 24.4 21.6 21.1 Z"
      />
      <ellipse
        cx={24}
        cy={23.5}
        rx={1.2}
        ry={0.6}
        className="agent-robot-tongue"
      />
    </g>
  );
};

/**
 * The CaseBlink robot as a flat bust: head, shoulders and chest plate on a
 * 48-unit grid, the shoulders running off the bottom edge. Each look is a hue
 * (colors in `styles/tokens.css`) and an expression. Size it with `className`
 * (e.g. `size-6`); unclassed it is 24px like a Lucide icon, so `[&_svg]`
 * sizing on buttons and menu items applies.
 */
export const AgentRobot = forwardRef<SVGSVGElement, Props>(function AgentRobot(
  {
    look = CASEY_LOOK,
    state = "idle",
    tile = false,
    title,
    className,
    ...rest
  },
  ref
) {
  const { expression } = look;
  const dots = expression !== "happy";
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-state={state}
      data-hue={look.hue}
      data-expression={expression}
      data-testid="agent-robot"
      {...rest}
      className={cn("agent-robot shrink-0 overflow-hidden", className)}
    >
      {tile ? (
        <rect width={48} height={48} className="agent-robot-tile" />
      ) : null}
      <rect
        x={3}
        y={34.5}
        width={42}
        height={22}
        rx={8}
        className="agent-robot-shade"
      />
      <rect
        x={3}
        y={33}
        width={42}
        height={22}
        rx={8}
        className="agent-robot-body"
      />
      <rect
        x={15.5}
        y={33}
        width={17}
        height={20}
        rx={3.5}
        className="agent-robot-face"
      />
      <g className="agent-robot-head">
        <rect
          x={8}
          y={7.5}
          width={32}
          height={26}
          rx={8}
          className="agent-robot-shade"
        />
        <rect
          x={8}
          y={6}
          width={32}
          height={26}
          rx={8}
          className="agent-robot-body"
        />
        <rect
          x={12.5}
          y={11}
          width={23}
          height={16.5}
          rx={4.5}
          className="agent-robot-face"
        />
        <ellipse
          cx={16.6}
          cy={22.4}
          rx={2.1}
          ry={1.2}
          className="agent-robot-blush"
        />
        <ellipse
          cx={31.4}
          cy={22.4}
          rx={2.1}
          ry={1.2}
          className="agent-robot-blush"
        />
        {expression === "wink" ? (
          <ClosedEye {...EYE_L} up={false} />
        ) : dots ? (
          <Eye
            {...EYE_L}
            big={expression === "curious"}
            side={expression === "lookSide"}
          />
        ) : (
          <ClosedEye {...EYE_L} up />
        )}
        {dots ? (
          <Eye
            {...EYE_R}
            big={expression === "curious"}
            side={expression === "lookSide"}
          />
        ) : (
          <ClosedEye {...EYE_R} up />
        )}
        <Mouth expression={expression} />
      </g>
    </svg>
  );
});
